import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input, Textarea } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Club } from "../api/types";

export function ClubsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Club>("/admin/clubs");
  const [editing, setEditing] = useState<Partial<Club> | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Club | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function save() {
    if (!editing?.name) {
      showToast("Name is required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      if (editing.id) {
        await apiRequest(`/admin/clubs/${editing.id}`, { method: "PATCH", body: editing });
      } else {
        await apiRequest("/admin/clubs", { method: "POST", body: editing });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save club", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/clubs/${pendingDelete.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  const columns: Column<Club>[] = [
    {
      header: "Club",
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.avatar_url ? <img src={row.avatar_url} className="h-9 w-9 rounded-full object-cover" alt="" /> : <div className="h-9 w-9 rounded-full bg-surface-raised" />}
          <span className="font-medium">{row.name}</span>
        </div>
      ),
    },
    { header: "Members", render: (row) => row.member_count.toLocaleString() },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Clubs">
      <div className="mb-4 flex justify-between">
        <p className="max-w-lg text-xs text-ink-faint">
          Clubs have no user-facing creation flow yet — admin management is currently the only way they're created.
        </p>
        <Button onClick={() => setEditing({})}>+ Add Club</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No clubs yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Club" : "Add Club"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input label="Avatar URL" value={editing?.avatar_url ?? ""} onChange={(e) => setEditing((p) => ({ ...p, avatar_url: e.target.value }))} />
          <Textarea label="Description" value={editing?.description ?? ""} onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title={`Delete "${pendingDelete?.name}"?`}
        description="This removes the club for all its members."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
